// import node_modules
import * as PropTypes from 'prop-types';
import * as React from 'react';
import { Heading, Text, Spacer } from '@storylinerlabs/design';
import { useTranslation } from 'react-i18next';

// import types
import { QuoteColor, SectionColor } from '../../types';

// import component
import AccordionCanvas from '../atoms/AccordionCanvas';
import Column from '../atoms/Column';
import EarlyAccessSection from '../elements/EarlyAccessSection';
import MainLayout from '../layouts/MainLayout';
import Quote from '../atoms/Quote';
import SEO from '../meta/SEO';
import Section from '../atoms/Section';
import StickyFeatureNav from '../navigations/StickyFeatureNav';

// define page
const FeaturesTemplate: React.FunctionComponent<{ hrefLang: string }> = ({ hrefLang }) => {
  // init hooks
  const { t } = useTranslation('features');

  // init render
  return (
    <>
      <SEO
        description={t('seo.description')}
        hrefLang={hrefLang}
        image={t('seo.image')}
        title={t('seo.title')}
      />

      <MainLayout>
        {/* Hero  */}
        <Section bgColor="anakiwa">
          {/* Navbar */}
          <Spacer height="64px" />
          <Spacer mobileHeight="25px" tabletHeight="25px" height="75px" />
        </Section>
        <Section bgColor="anakiwa">
          <Column cols="ml">
            <Heading alignment="center" tag="h1">
              {t('hero.title')}
            </Heading>
            <Text alignment="center" size="large">
              {t('hero.intro')}
            </Text>
          </Column>
        </Section>
        <Section bgColor="anakiwa">
          <Spacer mobileHeight="25px" tabletHeight="25px" height="75px" />
        </Section>

        {/* Navigation  */}
        <StickyFeatureNav />

        {/* Features */}
        {(
          t('features', { returnObjects: true }) as {
            id: string;
            description: string;
            image: string;
            items: { image: string; title: string; text: string }[];
            label: string;
            title: string;
          }[]
        ).map((feature, index) => {
          return (
            <AccordionCanvas
              accordionItems={feature.items}
              columnReverse={index % 2 !== 0}
              hasShadow={false}
              key={feature.id}
              sectionId={feature.id}
              IntroContent={() => (
                <Column>
                  <Heading alignment="left" tag="label">
                    {feature.label}
                  </Heading>
                  <Spacer mobileHeight="12px" tabletHeight="12px" height="12px" />
                  <Heading alignment="left" tag="h2">
                    {feature.title}
                  </Heading>
                  <Text alignment="left" size="large">
                    {feature.description}
                  </Text>
                  <Spacer mobileHeight="8px" tabletHeight="8px" height="8px" />
                </Column>
              )}
            />
          );
        })}
        <Section>
          <Spacer mobileHeight="50px" tabletHeight="50px" height="50px" />
        </Section>

        {/* Testimonial  */}
        <Section bgColor="energyYellow">
          <Spacer mobileHeight="50px" tabletHeight="50px" height="50px" />
        </Section>
        <Quote
          bgColor={t('testimonial.bgColor') as SectionColor}
          color={t('testimonial.color') as QuoteColor}
          colorDark={t('testimonial.colorDark') as QuoteColor}
          image={t('testimonial.image')}
          jobTitle={t('testimonial.jobTitle')}
          name={t('testimonial.name')}
          quote={t('testimonial.quote')}
          snippet={t('testimonial.snippet')}
          snippetDark={t('testimonial.snippetDark')}
        />
        <Section bgColor="energyYellow">
          <Spacer mobileHeight="50px" tabletHeight="50px" height="50px" />
        </Section>

        {/* Call to action  */}
        <Section>
          <Spacer height="100px" />
        </Section>
        <EarlyAccessSection />
        <Section>
          <Spacer height="100px" />
        </Section>
      </MainLayout>
    </>
  );
};

FeaturesTemplate.propTypes = {
  hrefLang: PropTypes.string.isRequired,
};

export default FeaturesTemplate;
